import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, Share2, ArrowUpRight, ArrowRight } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { productUrl, inrFormat } from "../lib/api";
import { useAuth } from "../lib/auth";
import useLiveCount from "../lib/useLiveCount";
import { useActivity } from "../lib/activity";
import ShareButton from "./ShareButton";
import BhegaButton from "./BhegaButton";

const HOVER_SPRING = { type: "spring", stiffness: 400, damping: 30 };
const STAMP_SPRING = { type: "spring", stiffness: 520, damping: 20 };

// Two labelled fields side by side. Labels share grid row 1 (bottom-aligned) and values share row 2,
// so values stay level even when one label wraps to two lines (Hindi/Gujarati).
function FieldPair({ labels, children }) {
  const values = React.Children.toArray(children);
  return (
    <div className="grid grid-cols-2 gap-x-3 items-end">
      {labels.map((l) => <span key={l} className="pass-label min-w-0">{l}</span>)}
      {values.map((v, i) => <div key={i} className="min-w-0 self-start">{v}</div>)}
    </div>
  );
}

function Value({ value, muted = false }) {
  return (
    <span className={`pass-value ${muted ? "pass-value-muted" : ""}`} title={typeof value === "string" ? value : undefined}>
      {value}
    </span>
  );
}

// "✓ YOU'RE IN" stamp. Plays once per session per product (when it scrolls into view), then stays static.
function Stamp({ animate, onDone }) {
  const reduce = useReducedMotion();
  const content = <><span>✓ You&apos;re</span><span>in</span></>;
  if (!animate) return <div className="pass-stamp" aria-hidden>{content}</div>;
  return (
    <motion.div
      className="pass-stamp"
      aria-hidden
      initial={reduce ? { opacity: 0, rotate: -8 } : { scale: 1.8, opacity: 0, rotate: -24 }}
      whileInView={{ scale: 1, opacity: 1, rotate: -8 }}
      viewport={{ once: true, amount: 0.6 }}
      transition={reduce ? { duration: 0.15, delay: 0.15 } : { ...STAMP_SPRING, delay: 0.15 }}
      onAnimationComplete={onDone}
    >
      {content}
    </motion.div>
  );
}

// Product card styled as a "Buying Pass". The Bhega button is the hero of every pass.
export default function ProductCardText({ product, onTagClick }) {
  const navigate = useNavigate();
  const { city } = useAuth();
  const activity = useActivity();
  const count = useLiveCount(product);
  const [showShare, setShowShare] = useState(false);
  const [stampMode, setStampMode] = useState(null); // null | "anim" | "static"
  const [fistFlash, setFistFlash] = useState(0);
  const prevCount = useRef(count);

  const joined = !!activity?.joinedIds?.has(product.id);
  const room = joined ? (activity?.rooms || []).find((r) => r.product_id === product.id) : null;
  const unread = room?.unread_count || 0;

  // Briefly flash the fist when the buyer count rises.
  useEffect(() => {
    if (count > prevCount.current) setFistFlash((n) => n + 1);
    prevCount.current = count;
  }, [count]);

  // Decide whether the stamp animates (not yet seen this session) or renders static.
  // Only read here; the key is written once the animation has actually played (StrictMode-safe).
  const stampKey = `bhega:stamped:${product.id}`;
  useEffect(() => {
    if (!joined) { setStampMode(null); return; }
    let seen = true;
    try { seen = !!window.sessionStorage.getItem(stampKey); } catch (_) { seen = true; }
    setStampMode(seen ? "static" : "anim");
  }, [joined, stampKey]);
  const markStamped = () => {
    try { window.sessionStorage.setItem(stampKey, "1"); } catch (_) { /* noop */ }
  };

  const websiteUrl = (product.website_url || "").trim();
  const commentCount = product.comment_count ?? 0;
  const variant = product.typology || product.variant;
  const name = product.product || product.name;
  const brandLine = [product.brand, variant].filter(Boolean).join(" · ");
  const typeLabel = product.category_label || product.category || "—";
  const cityLabel = product.scope === "global" ? "All India" : (city || "All India");

  const dealPrice = Number(product.lowest_deal_price) || 0;
  const showroom = Number(product.price_min) || 0;
  const saving = dealPrice > 0 && showroom > dealPrice ? showroom - dealPrice : 0;

  // Tag chips were removed from the pass; the handler stays so callers passing onTagClick keep working.
  // eslint-disable-next-line no-unused-vars
  const handleTagClick = (e, t) => {
    e.preventDefault(); e.stopPropagation();
    if (onTagClick) onTagClick(t);
    else navigate(`/browse?q=${encodeURIComponent(t)}`);
  };

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}${productUrl(product)}`
    : "";
  const shareSubtitle = joined
    ? `I'm buying ${name} with ${count} people on BHEGA ✊ Join my group, we'll get a better price together.`
    : `Join ${count} buyers ✊ buying together ${product.brand} ${name}`;
  const actionCls = "inline-flex items-center justify-center gap-1.5 min-h-[48px] px-2 text-center leading-tight text-[length:var(--fs-small)] font-semibold text-muted-foreground hover:text-foreground hover:bg-raised active:bg-raised transition-colors";

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      className="bhega-card bhega-card-hover pass flex flex-col h-full"
      whileHover={{ y: -2 }}
      transition={HOVER_SPRING}
    >
      <div className={`pass-head ${joined ? "pass-head-joined" : ""}`}>
        <span className="truncate">BHEGA · Buying Pass</span>
        {commentCount > 0 && (
          <span className="inline-flex items-center gap-1 num shrink-0">
            <MessageCircle size={12} aria-hidden /> {commentCount}<span className="sr-only"> comments</span>
          </span>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col gap-4">
        <div className="min-w-0 relative">
          <span className="pass-label">Buying</span>
          <Link to={productUrl(product)} className="block min-h-[48px] group/title">
            <h3
              className={`pass-key line-clamp-2 sm:min-h-[2.5em] [overflow-wrap:anywhere] ${joined ? "pr-16" : ""} group-hover/title:underline decoration-[var(--bhega-primary)] decoration-[3px] underline-offset-4`}
              data-testid={`product-name-${product.id}`}
              title={name}
            >
              {name}
            </h3>
          </Link>
          {brandLine && (
            <div className="text-[length:var(--fs-small)] text-muted-foreground font-medium truncate" title={brandLine}>
              <span className="text-foreground font-semibold">{product.brand}</span>{variant ? ` · ${variant}` : ""}
            </div>
          )}
          {joined && stampMode && <Stamp animate={stampMode === "anim"} onDone={markStamped} />}
        </div>

        <FieldPair labels={["Type", "Your city"]}>
          <Value value={typeLabel} />
          <Value value={cityLabel} />
        </FieldPair>

        <FieldPair labels={["Buyers", "Group price"]}>
            <span className="pass-key num flex items-center gap-1.5 overflow-hidden" title={`${count} buyers`}>
              <motion.span
                aria-hidden
                key={`fist-${fistFlash}`}
                className="inline-block"
                animate={fistFlash ? { scale: [1, 1.15, 1] } : undefined}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                ✊
              </motion.span>
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={count}
                  className="inline-block"
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -10, opacity: 0 }}
                >
                  {count}
                </motion.span>
              </AnimatePresence>
            </span>
            {dealPrice > 0 ? (
              <>
                <span className="pass-value" title={inrFormat(dealPrice)}>{inrFormat(dealPrice)}</span>
                {saving > 0 && (
                  <span className="block text-[length:var(--fs-small)] font-bold text-[var(--bhega-success)] truncate">Save {inrFormat(saving)}</span>
                )}
              </>
            ) : (
              <span className="pass-value pass-value-muted">No price yet</span>
            )}
        </FieldPair>

      </div>

      <div className="pass-perf" aria-hidden><span /></div>

      <div className="px-4 pt-2 pb-4">
        <p className="text-[length:var(--fs-small)] text-muted-foreground mb-2">
          {count > 0
            ? <><strong className="text-foreground num">{count}</strong> buyer{count === 1 ? "" : "s"} united to buy this</>
            : <>Be the first to raise your fist</>}
        </p>
        <BhegaButton product={product} count={count} size="lg" testId={`bhega-join-icon-${product.id}`} countTestId={`product-card-bhega-${product.id}`} />
        <p className="min-h-[1.5em] text-[length:var(--fs-small)] text-muted-foreground mt-2 text-center" aria-hidden={joined}>
          {!joined && "Free. No payment needed."}
        </p>
      </div>

      <div className="flex border-t border-border divide-x divide-border" data-testid={`product-actions-${product.id}`}>
        <Link to={productUrl(product)} className={`${actionCls} flex-[1.4]`} data-testid={`join-room-btn-${product.id}`}>
          {unread > 0 ? (
            <span className="inline-flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--bhega-primary)]" aria-hidden />
              Room · <span className="num">{unread}</span> new
            </span>
          ) : <><MessageCircle size={13} aria-hidden /> Room</>}
          <ArrowRight size={13} aria-hidden />
        </Link>
        {websiteUrl ? (
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className={`${actionCls} flex-[0.8]`} data-testid={`about-btn-${product.id}`} title={websiteUrl}>
            About <ArrowUpRight size={13} aria-hidden />
          </a>
        ) : (
          <Link to={productUrl(product)} className={`${actionCls} flex-[0.8]`} data-testid={`about-btn-${product.id}`}>About</Link>
        )}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowShare(true); }}
          className={`${actionCls} flex-[1.1]`}
          data-testid={`action-share-${product.id}`}
        >
          <Share2 size={13} aria-hidden /> {joined ? "Share pass" : "Share"}
        </button>
      </div>

      {showShare && (
        <ShareButton
          url={shareUrl}
          title={`${name} on BHEGA`}
          subtitle={shareSubtitle}
          defaultOpen
          onClose={() => setShowShare(false)}
          testId={`card-share-${product.id}`}
        />
      )}
    </motion.article>
  );
}

// Loading pass: same shape and classes as the real card so the grid doesn't jump.
export function ProductCardSkeleton() {
  const row = (
    <div className="grid grid-cols-2 gap-3">
      <div><div className="skeleton h-3 w-12" /><div className="skeleton h-[22px] w-3/4 mt-1" /></div>
      <div><div className="skeleton h-3 w-16" /><div className="skeleton h-[22px] w-2/3 mt-1" /></div>
    </div>
  );
  return (
    <div className="bhega-card pass flex flex-col h-full" aria-hidden>
      <div className="pass-head"><span className="opacity-60">BHEGA · Buying Pass</span></div>
      <div className="p-4 flex-1 flex flex-col gap-4">
        <div>
          <div className="skeleton h-3 w-14" />
          <div className="skeleton h-[29px] w-3/4 mt-1" />
          <div className="skeleton h-4 w-1/2 mt-[19px] sm:mt-[33px]" />
        </div>
        {row}
        <div className="grid grid-cols-2 gap-3">
          <div><div className="skeleton h-3 w-12" /><div className="skeleton h-[29px] w-1/2 mt-1" /></div>
          <div><div className="skeleton h-3 w-20" /><div className="skeleton h-[22px] w-2/3 mt-1" /></div>
        </div>
      </div>
      <div className="pass-perf"><span /></div>
      <div className="px-4 pt-2 pb-4">
        <div className="skeleton h-[19px] w-2/3 mb-2" />
        <div className="skeleton h-16 w-full" />
        <div className="min-h-[1.5em] mt-2 flex justify-center"><div className="skeleton h-[19px] w-1/3" /></div>
      </div>
      <div className="flex border-t border-border min-h-[48px]" />
    </div>
  );
}
